/* eslint-disable @spaced-out/i18n/no-static-labels */
import * as React from 'react';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {isLanguageSupported} from 'src/utils/language-support';

import css from './language-support.css';
import {type MenuOption} from '@spaced-out/ui-design-system';


export const getLanguageLabel = (key: string): string => {
  let label = 'English';
  switch (key) {
    case 'en':
      label = 'English';
      break;
    case 'fr-CA':
      label = 'Français canadien';
      break;
    case 'es-419':
      label = 'Español latinoamericano';
      break;
    case 'it':
      label = 'Italiano';
      break;
    case 'fr':
      label = 'Français';
      break;
    case 'es':
      label = 'Español';
      break;
    case 'de':
      label = 'Deutsch';
      break;
    case 'hu':
      label = 'Magyar';
      break;
    case 'ja':
      label = '日本語';
      break;
    case 'nl':
      label = 'Nederlands';
      break;
    case 'pt':
      label = 'Português';
      break;
    case 'sv':
      label = 'Svenska';
      break;
  }
  return label;
};

export const LanguageSupport = ({
  language,
  isExternalApp = false,
  persistLanguage = true,
  onLanguageChange,
}: {
  language?: string;
  isExternalApp?: boolean;
  persistLanguage?: boolean;
  onLanguageChange?: (language: string) => void;
}) => {
  const {
    t,
    language: initializedLanguage,
    updateLanguage,
    getCurrentLanguage,
  } = useLabelTranslation();

  const initialLanguage =
    language ||
    getCurrentLanguage({
      supportBrowserLanguage: true,
      supportDefaultLanguage: true,
      supportLocalStorageLanguage: isExternalApp,
      supportApiSelectedLanguage: true,
    });
  const [selectedLanguage, setSelectedLanguage] = React.useState({
    key: initialLanguage,
    label: getLanguageLabel(initialLanguage),
  });

  const onLanguageSelect = async (
    option:
      | MenuOption
      | {
          key: string;
          label: string;
        },
  ) => {
    // external apps shouldn't send data to API
    // signin, referral-external, scheduler booking
    await updateLanguage({
      language: option.key,
      persistLanguage,
      sendUpdatedLanguageToAPI: false,
    });
    // @ts-expect-error TS2345
    setSelectedLanguage(option);
    onLanguageChange?.(option.key);
  };

  const loadLanguageAndSetOption = async (language: string) => {
    if (language === initializedLanguage) {
      return;
    }
    // create language object with key and localized label
    const newLanguage = {
      key: language,
      label: getLanguageLabel(language),
    } as const;
    // update the selected language in dropdown and i18n instance
    onLanguageSelect(newLanguage);
  };

  // Effect to handle dynamic language changes from external sources (e.g., API)
  // This ensures the app renders with the new language and updates the dropdown selection
  // Currently used in the scheduler app where user language is fetched from API and we need to render the app with the new language
  React.useEffect(() => {
    const loadLanguage = async () => {
      // Check if language is supported and different from current selection
      if (
        language &&
        isLanguageSupported(language) &&
        language !== selectedLanguage.key
      ) {
        loadLanguageAndSetOption(language);
      }
    };

    loadLanguage();
  }, [language]);

  React.useEffect(() => {
    if (
      selectedLanguage.key &&
      isLanguageSupported(selectedLanguage.key) &&
      selectedLanguage.key !== initialLanguage
    ) {
      loadLanguageAndSetOption(selectedLanguage.key);
    }
  }, [selectedLanguage.key]);

  return (
    <div className={css.container}>
      <SimpleDropdown
        onChange={onLanguageSelect}
        iconLeftName="language"
        iconLeftType="solid"
        placeholder={t('Select_language', 'Select language')}
        value={selectedLanguage.label}
        selectedKeys={[initialLanguage]}
        options={[
          {label: 'English', key: 'en'},
          {label: 'Français canadien', key: 'fr-CA'},
          {label: 'Español latinoamericano', key: 'es-419'},
          {label: 'Italiano', key: 'it'},
          {label: 'Français', key: 'fr'},
          {label: 'Español', key: 'es'},
          {label: 'Deutsch', key: 'de'},
          {label: 'Magyar', key: 'hu'},
          {label: '日本語', key: 'ja'},
          {label: 'Nederlands', key: 'nl'},
          {label: 'Português', key: 'pt'},
          {label: 'Svenska', key: 'sv'},
        ]}
        size="small"
        // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
        selectedKeys={[selectedLanguage.key ?? '']}
        classNames={{wrapper: css.dropdown}}
      />
    </div>
  );
};
